
import { Component, Vue } from "vue-property-decorator";
import { GAMETYPE_ID, Season } from "@/models/GameType";
import ProgressDialog from "@/components/Base/ProgressDialog.vue";
import { DateTime, Interval } from "luxon";
import { City } from "@/models/City";
import ImageBox from "@/components/Base/ImageBox.vue";
import { DropDownItem } from "@/models/Base/GlobalData";
import { Q_QUEST_TYPE } from "@/models/MapPois/Quest/QuestsEnum";
import { Validations } from "vuelidate-property-decorators";
import ImageSelector from "@/components/Base/ImageSelector.vue";
import { required, minLength, maxLength } from "vuelidate/lib/validators";

@Component({
  components: { ProgressDialog, ImageBox, ImageSelector },
})
class CreateGame extends Vue {
  adminName = "";
  errorMessage: string[] = [];
  warningMessage: string[] = [];
  selectedCity = "Wrest Park Debug 001";
  selectedSeason = "";
  startDate = DateTime.now()
    .plus({ days: 1 })
    .toString();
  min = new Date();
  max = DateTime.now()
    .plus({ days: 720 })
    .toString();
  endDate = DateTime.now()
    .plus({ days: 1 })
    .toString();
  endTime = "15:00";
  startTime = "08:00";
  selectedGameType = "quest";
  getImage = "";
  showImageSelectorBool = false;

  get getGameType(): DropDownItem[] {
    return [{ text: GAMETYPE_ID.QUEST, value: GAMETYPE_ID.QUEST }];
  }
  get getCities(): string[] {
    const results: string[] = this.$store.state.cityStore.city.map(
      (t: City) => t.name.en
    );
    return results.sort();
  }

  get getGameTypes(): string[] {
    return this.$store.state.gameStore.gameType.map((t: Season) => t.name.en);
  }

  get getCityDataInfo(): City {
    return this.$store.state.cityStore.city.find(
      (x: City) => x.name.en === this.selectedCity
    );
  }

  get getCityData(): City {
    return this.$store.state.cityStore.clonedCity;
  }
  get getSeasonUid(): string {
    return this.$store.state.gameStore.gameType.find(
      (t: Season) => t.name.en === this.selectedSeason
    ).slug;
  }

  created(): void {
    this.$store.dispatch("gameStore/getGameTypes", this.selectedGameType);
    this.$store.dispatch("cityStore/getClonedCity", this.getCityDataInfo.slug);
    if (this.getCityData.tZone !== undefined) {
      this.startDate = DateTime.now()
        .plus({ days: 1 })
        .setZone(this.getCityData.tZone!.timeZoneId)
        .toString();
      this.endDate = DateTime.now()
        .plus({ days: 1 })
        .setZone(this.getCityData.tZone!.timeZoneId)
        .toString();
    }
    this.$v.$touch();
    this.checkWarningDate();
  }

  cancel(): void {
    this.$emit("closeDialog");
  }
  saveGame(): void {
    this.$v.$touch();
    this.errorMessage = [];
    if (this.adminName.trim().length < 3) {
      this.errorMessage.push("Please enter a valid admin name");
    }
    if (this.selectedCity.length < 1) {
      this.errorMessage.push("Please enter a valid city");
    }
    if (this.selectedSeason.length < 1) {
      this.errorMessage.push("Please enter a valid season");
    }
    if (this.selectedGameType.length < 1) {
      this.errorMessage.push("Please enter a valid game type");
    }
    if (this.getCityData.tZone === undefined) {
      this.errorMessage.push("Selected City does not hava a valid Timezone");
    }
    if (this.getCityData.tZone !== undefined) {
      if (this.checkDate()) {
        this.errorMessage.push("The end date must come after the start date");
      }
    }

    if (this.errorMessage.length == 0) {
      this.$emit("closeDialog");
      this.$bvModal.show("team-modal-spinner");
      var data = {
        admin_name: this.adminName,
        city_uid: this.getCityData.slug,
        game_type: this.selectedGameType,
        seas_uid: this.getSeasonUid,
        start_dt: this.getUTCTime(this.startDate.toString(), this.startTime),
        end_dt: this.getUTCTime(this.endDate.toString(), this.endTime),
        lat: this.getCityData.centre_point.latitude,
        lng: this.getCityData.centre_point.longitude,
        corpImage: this.getImage,
        evergreen: {
          isActive: false,
          isClone: false,
        },
      };
      this.$store.dispatch("gameStore/insertNewGame", data).then((result) => {
        if (data.game_type === GAMETYPE_ID.QUEST) {
          var questData = {
            gameKey: result,
            title: "Main Quest",
            desc: "A Default Main Quest",
            type: Q_QUEST_TYPE.MAIN,
            imgs: [],
            city: this.getCityData,
          };
          this.$store.dispatch("questStore/insertFirstQuest", questData);
        }
        this.$bvModal.hide("team-modal-spinner");
      });
    }
  }

  setOptionSelected(value: string): void {
    this.selectedGameType = value;
    this.$store.commit("gameStore/gameTypeSelect", value);
    this.$store.dispatch("gameStore/getGameTypes", value);
  }

  getUTCTime(date: string, time: string): string {
    var time2 = DateTime.fromISO(time);
    if (this.getCityData.tZone !== undefined) {
      return DateTime.fromISO(date.toString(), {
        zone: this.getCityData.tZone!.timeZoneId,
      })
        .set({
          hour: time2.hour,
          minute: time2.minute,
          second: 0,
          millisecond: 0,
        })
        .toISO();
    } else {
      return DateTime.fromISO(date.toString())
        .set({
          hour: time2.hour,
          minute: time2.minute,
          second: 0,
          millisecond: 0,
        })
        .toISO();
    }
  }

  get getStartDateInShortFormat(): string {
    return (
      DateTime.fromISO(this.startDate.toString()).toFormat("dd/MM/yyyy") +
      " @ " +
      this.startTime
    );
  }
  get getEndDateInShortFormat(): string {
    return (
      DateTime.fromISO(this.endDate.toString()).toFormat("dd/MM/yyyy") +
      " @ " +
      this.endTime
    );
  }

  get getTimeZone(): string {
    if (this.getCityData.tZone === undefined) {
      return "Invalid ";
    } else {
      return (
        this.getCityData.tZone!.timeZoneId +
        ", " +
        this.getCityData.tZone!.timeZoneName +
        " (" +
        DateTime.fromISO(this.startDate.toString(), {
          zone: this.getCityData.tZone!.timeZoneId,
        }).offset /
          60 +
        ")"
      );
    }
  }

  checkWarningDate(): void {
    this.warningMessage = [];
    var todayDate = new Date();
    var startDate = DateTime.fromISO(
      this.getUTCTime(this.startDate, this.startTime)
    );
    var endDate = DateTime.fromISO(this.getUTCTime(this.endDate, this.endTime));
    const diff = Interval.fromDateTimes(todayDate, startDate);
    if (startDate > endDate) {
      this.warningMessage.push(
        "Please make sure the end date is in front of the start date"
      );
    }
    if (diff.length("hours") < 48) {
      this.warningMessage.push(
        "Date is less than 48 hours away. Please be careful as this data might be purged"
      );
    }
  }

  checkDate(): boolean {
    if (
      DateTime.fromISO(this.getUTCTime(this.startDate, this.startTime)) <
      DateTime.fromISO(this.getUTCTime(this.endDate, this.endTime))
    ) {
      return false;
    } else {
      return true;
    }
  }

  onValuedChanged(name: string): void {
    this.selectedCity = name;
    this.$store.dispatch("cityStore/getClonedCity", this.getCityDataInfo.slug);
  }

  returnImagePath(path: string): void {
    this.getImage = this.getImagePath + "" + path;
    this.showImageSelectorBool = false;
  }

  showImageSelector(): void {
    this.showImageSelectorBool = true;
  }
  handleCloseButtonPress(): void {
    this.showImageSelectorBool = false;
  }

  clearImage(): void {
    this.getImage = "";
  }

  get getImagePath(): string {
    return "/corporate/logos/";
  }

  @Validations()
  validations = {
    adminName: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(160),
    },
    selectedCity: {
      required,
      minLength: minLength(1),
    },
    selectedSeason: {
      required,
      minLength: minLength(1),
    },
  };
}

export default CreateGame;
